import { screeningSchema } from './utils/screeningSchema';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { supabaseShowTimes, supabaseMovies } from './supabase';
import { THEATERS, THEATER_ADDRESSES, getTicketLink } from './constants';
import { POPUP_CINEMA_ID, demoEnabled, DEMO_EVENTS } from './utils/popupDemo';
import { VEEZI_PREVIEW, VEEZI_PREVIEW_CINEMA, VEEZI_PREVIEW_SUPPRESS, loadVeeziPreview } from './utils/veeziPreview';
import { posterSrc } from './utils/poster';
import WatchlistHeart from './components/WatchlistHeart';
import AuthModal from './components/AuthModal';
import { readFilmBootstrap } from './utils/filmBootstrap';
import { screeningNote } from './utils/screeningNotes';

const createGoogleCalendarLink = (movieTitle, theater, startTime) => {
  const eventDate = new Date(startTime);
  const endDate = new Date(eventDate.getTime() + (3 * 60 * 60 * 1000));
  const startDateTime = eventDate.toISOString().replace(/[:-]/g, '').replace('.000', 'Z');
  const endDateTime = endDate.toISOString().replace(/[:-]/g, '').replace('.000', 'Z');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${movieTitle} at ${theater}`,
    details: `Movie showing of ${movieTitle} at ${theater}`,
    location: theater,
    dates: `${startDateTime}/${endDateTime}`
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

function FilmPage() {
  const { slug } = useParams();
  const [movie, setMovie] = useState(() => readFilmBootstrap(slug));
  const [upcomingShowtimes, setUpcomingShowtimes] = useState([]);
  const [pastShowtimes, setPastShowtimes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    setLoadError(false);
    setMovie(readFilmBootstrap(slug));
    setUpcomingShowtimes([]);
    setPastShowtimes([]);
    async function fetchData() {
      try {
        let movieData = readFilmBootstrap(slug) || await supabaseMovies.getBySlug(slug);
        if (cancelled) return;

        // Local-dev preview: serve the Anthology film from the static export
        // (its record + showtimes) instead of Supabase — see utils/veeziPreview.
        let previewShowtimes = null;
        if (VEEZI_PREVIEW) {
          const p = await loadVeeziPreview();
          const pm = p && Object.values(p.movies || {}).find(m => m.slug === slug);
          if (pm) {
            movieData = { ...(movieData || {}), ...pm };
            previewShowtimes = (p.showtimes || []).filter(st => String(st.movie_id) === String(pm.id));
          }
        }

        if (cancelled) return;
        if (!movieData) {
          setNotFound(true);
          setIsLoading(false);
          return;
        }
        setMovie(movieData);

        let allShowtimes = await supabaseShowTimes.getAllByMovieId(movieData.id);
        if (cancelled) return;
        if (previewShowtimes) {
          allShowtimes = [
            ...allShowtimes.filter(st => st.theaterId !== VEEZI_PREVIEW_CINEMA && st.cinema_id !== VEEZI_PREVIEW_CINEMA
              // Multi-venue preview (AMC+Regal cleanup): the other grouped cinemas' live
              // rows are collapsed into the preview venue — drop them or they duplicate.
              && !VEEZI_PREVIEW_SUPPRESS.has(String(st.theaterId)) && !VEEZI_PREVIEW_SUPPRESS.has(String(st.cinema_id))),
            ...previewShowtimes,
          ];
        }
        const now = new Date();
        // Local demo (?demo=1 on a dev host): inject the shared sample pop-up rows
        // for this film client-side so the pop-up section can be QA'd pre-launch.
        // Nothing is written anywhere; see utils/popupDemo.
        if (demoEnabled()) {
          allShowtimes = [...allShowtimes, ...DEMO_EVENTS.filter(ev => String(ev.movie_id) === String(movieData.id))];
        }
        const upcoming = allShowtimes
          .filter(st => new Date(st.start_at) >= now)
          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
        const past = allShowtimes
          .filter(st => new Date(st.start_at) < now)
          .sort((a, b) => new Date(b.start_at) - new Date(a.start_at));

        setUpcomingShowtimes(upcoming);
        setPastShowtimes(past);
      } catch (error) {
        console.error('Error loading film page:', error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [slug]);

  if (isLoading && !movie) return <div className="movie-detail"><p>Loading...</p></div>;
  if (loadError && !movie) return <div className="movie-detail"><p>This film is temporarily unavailable.</p><button onClick={() => window.location.reload()}>Try again</button></div>;
  if (notFound) return (
    <div className="movie-detail">
      <a href="/" style={{ textDecoration: 'none', color: '#d4a843', fontSize: '14px' }}>← return to homepage</a>
      <h2>Film not found</h2>
      <p>No film matches this URL. It may not have screened yet.</p>
    </div>
  );

  // Derive year from release_dates
  const releaseYear = Number(movie.release_year) || (movie.release_dates
    ? (() => {
        try {
          const dates = Object.values(movie.release_dates).flat().map(r => r.date).filter(Boolean).sort();
          return dates.length > 0 ? Number(String(dates[0]).slice(0, 4)) || null : null;
        } catch { return null; }
      })()
    : null);

  // Extract YouTube video ID from poster_path (stored as YouTube thumbnail URL)
  let trailerYoutubeId = null;
  if (movie.poster_path) {
    const ytMatch = movie.poster_path.match(/youtube\.com\/vi\/([^/]+)/);
    if (ytMatch) trailerYoutubeId = ytMatch[1];
  }
  const trailerSrc = trailerYoutubeId ? `https://www.youtube.com/embed/${trailerYoutubeId}` : null;
  const posterUrl = posterSrc(movie.poster_path);

  // Pop-up rows (Film Clubs portal) get their OWN marked section below the
  // theater list (Josh 8/3: "a dividing line… clearly mark that it's a pop-up
  // screening at a bar") — never interleaved with theater screenings.
  const theaterRows = upcomingShowtimes.filter(st => st.cinema_id !== POPUP_CINEMA_ID);
  const popupRows = upcomingShowtimes.filter(st => st.cinema_id === POPUP_CINEMA_ID);

  // Group upcoming showtimes by date
  const upcomingByDate = theaterRows.reduce((acc, st) => {
    const date = new Date(st.start_at).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(st);
    return acc;
  }, {});
  const popupByDate = popupRows.reduce((acc, st) => {
    const date = new Date(st.start_at).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(st);
    return acc;
  }, {});

  // Past screening summary
  const pastTheaters = [...new Set(pastShowtimes.map(st => st.theater))];
  const lastScreening = pastShowtimes[0];

  // Admin "Edit Title" support: customTitle from moderation_info takes
  // precedence over the raw movies.title, mirroring how renderMovieTitle in
  // CalendarView builds its displayed title. Without this, the admin edit
  // shows up on the calendar but not on the film detail page.
  const customTitle = movie.moderation_info?.customTitle;
  const cleanBase = (customTitle || movie.title || '').replace(/\s*\(\d{4}\)/g, '').trim();
  const displayTitle = `${cleanBase}${releaseYear ? ` (${releaseYear})` : ''}`;
  const metaTitle = `${displayTitle} Screenings in NYC | Film Revival NYC`;
  const metaDescription = movie.synopsis
    ? `${movie.synopsis.slice(0, 140)}... Find upcoming screenings in NYC.`
    : `Find upcoming screenings of ${displayTitle}${movie.director ? `, directed by ${movie.director},` : ''} at NYC revival cinemas. Showtimes, theaters, and tickets.`;
  const canonicalUrl = `https://filmrevivalnyc.com/films/${slug}`;

  // Schema
  const movieSchema = {
    "@context": "https://schema.org",
    "@type": "Movie",
    "name": movie.title,
    ...(releaseYear && { "dateCreated": String(releaseYear) }),
    ...(movie.synopsis && { "description": movie.synopsis.slice(0, 300) }),
    ...(movie.director && { "director": { "@type": "Person", "name": movie.director } }),
    ...(movie.genres && Array.isArray(movie.genres) && { "genre": movie.genres }),
    ...(movie.runtime && { "duration": `PT${movie.runtime}M` }),
    ...(movie.cast_list && Array.isArray(movie.cast_list) && {
      "actor": movie.cast_list.map(name => ({ "@type": "Person", "name": name }))
    }),
    "url": canonicalUrl
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://filmrevivalnyc.com/" },
      { "@type": "ListItem", "position": 2, "name": "Films", "item": "https://filmrevivalnyc.com/films" },
      { "@type": "ListItem", "position": 3, "name": movie.title, "item": canonicalUrl }
    ]
  };

  const videoSchema = trailerYoutubeId ? {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": `${movie.title} Trailer`,
    "description": `Official trailer for ${displayTitle}`,
    "thumbnailUrl": `https://img.youtube.com/vi/${trailerYoutubeId}/hqdefault.jpg`,
    "embedUrl": trailerSrc,
    ...(releaseYear && { "uploadDate": `${releaseYear}-01-01` })
  } : null;

  const eventSchemas = upcomingShowtimes.map(st => {
    const venue = THEATERS.find(t => String(t.id) === String(st.cinema_id || st.theaterId))
      || THEATERS.find(t => t.name === st.theater);
    // Seasonal and roving sources need an event-specific address, not an HQ.
    const address = venue && !venue.id.startsWith('outdoor-') && venue.id !== '81351'
      ? THEATER_ADDRESSES[venue.id] : null;
    return screeningSchema(st, movie, {
      address, url:canonicalUrl, workPresented:movieSchema,
      bookingUrl:getTicketLink(st.booking_link, venue?.id),
    });
  }).filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={`${displayTitle} — Film Revival NYC`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="video.movie" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={posterUrl || "https://filmrevivalnyc.com/FILM_REVIVAL_NY_OG.jpg"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${displayTitle} — Film Revival NYC`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={posterUrl || "https://filmrevivalnyc.com/FILM_REVIVAL_NY_OG.jpg"} />
        <link rel="canonical" href={canonicalUrl} />

        <script type="application/ld+json">{JSON.stringify(movieSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {videoSchema && <script type="application/ld+json">{JSON.stringify(videoSchema)}</script>}
        {eventSchemas.map((schema, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      <div className="movie-detail">
        <div style={{ textAlign: 'left', marginBottom: '20px' }}>
          <a href="/" style={{ textDecoration: 'none', color: '#d4a843', fontSize: '14px' }}>
            ← return to homepage
          </a>
        </div>

        {/* Header with poster + info */}
        <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {posterUrl && !trailerSrc && (
            <img
              src={posterUrl}
              alt={`${movie.title} poster`}
              style={{ width: '220px', height: '124px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }}
            />
          )}
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h2 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {displayTitle}
              <WatchlistHeart movieTitle={movie.title} movieId={movie.id} size={22} showAuthModal={() => setShowAuth(true)} />
            </h2>
            {(movie.director || movie.runtime) && (
              <p style={{ color: '#888', margin: '0 0 6px 0', fontSize: '0.9rem' }}>
                {movie.director && <>Directed by <strong style={{ color: '#8a8272' }}>{movie.director}</strong></>}
                {movie.director && movie.runtime && ' | '}
                {movie.runtime && `${Math.floor(movie.runtime / 60)}h ${movie.runtime % 60}m`}
              </p>
            )}
            {movie.genres && Array.isArray(movie.genres) && movie.genres.length > 0 && (
              <p style={{ color: '#888', margin: '0 0 6px 0', fontSize: '0.85rem' }}>
                {movie.genres.join(', ')}
              </p>
            )}
            {movie.cast_list && Array.isArray(movie.cast_list) && movie.cast_list.length > 0 && (
              <p style={{ color: '#888', margin: '0 0 10px 0', fontSize: '0.85rem' }}>
                Starring: {movie.cast_list.join(', ')}
              </p>
            )}
            {movie.synopsis ? (
              // pre-line: program blurbs (Anthology Essential Cinema etc.) carry
              // one-film-per-line formatting that plain <p> rendering collapsed
              // into a wall of text (Josh 2026-07-24).
              <p style={{ color: '#555', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-line' }}>{movie.synopsis}</p>
            ) : (
              <p style={{ color: '#999', fontStyle: 'italic', margin: 0 }}>No synopsis available.</p>
            )}
          </div>
        </div>

        {/* Trailer */}
        {trailerSrc && (
          <div className="trailer-container" style={{ marginBottom: '24px' }}>
            <iframe
              width="560"
              height="315"
              src={trailerSrc}
              title={`${movie.title} Trailer`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ maxWidth: '100%' }}
            />
          </div>
        )}

        {/* Upcoming Showtimes */}
        <h3>Upcoming Screenings</h3>
        {isLoading ? <p>Loading screening times…</p> : loadError ? <p>Screening times are temporarily unavailable. <button onClick={() => window.location.reload()}>Try again</button></p> : Object.entries(upcomingByDate).length > 0 ? (
          Object.entries(upcomingByDate)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .map(([date, dateShowtimes]) => (
              <div key={date} className="date-showtimes">
                <h4>{new Date(date).toLocaleDateString('en-US', { weekday: 'long' })}, {date}</h4>
                <ul className="showtimes-list">
                  {dateShowtimes
                    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
                    .map(st => {
                      const time = new Date(st.start_at);
                      const timeLabel = time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                      const calLink = createGoogleCalendarLink(movie.title, st.theater, st.start_at);
                      const theater = THEATERS.find(t => t.name === st.theater);
                      const eventNote = screeningNote(st);
                      return (
                        <li key={st.start_at + st.theater} style={eventNote ? { flexWrap: 'wrap' } : undefined}>
                          <strong>{timeLabel}</strong> – {theater ? (
                            <Link
                              to={`/theater/${theater.slug}`}
                              style={{ textDecoration: 'none', color: '#d4a843', fontWeight: '500' }}
                              onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                              onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                            >
                              {st.theater}
                            </Link>
                          ) : st.theater}
                          {/* Whitelisted series pill (Silent Clowns) — series stays
                              hidden for everything else (Josh's rule). */}
                          {st.series === 'Silent Clowns Film Series' && (
                            <span style={{ marginLeft: '6px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', background: '#d4a843', color: '#1a1714', borderRadius: '4px', padding: '2px 7px', verticalAlign: 'middle' }}>SILENT CLOWNS · LIVE PIANO</span>
                          )}
                          {st.film_format && (
                            ['35mm', '70mm', '16mm'].includes(st.film_format) ? (
                              <Link
                                to="/35mm-nyc"
                                style={{ marginLeft: '6px', fontSize: '0.8rem', color: '#d4a843', textDecoration: 'none' }}
                                title="See all film print screenings in NYC"
                                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                              >
                                ({st.film_format})
                              </Link>
                            ) : (
                              <span style={{ marginLeft: '6px', fontSize: '0.8rem', color: '#888' }}>({st.film_format})</span>
                            )
                          )}
                          {eventNote && <p style={{ flexBasis: '100%', fontSize: '0.9rem', margin: '4px 0', lineHeight: 1.5 }}>{eventNote}</p>}
                          {!eventNote && ' – '}
                          <span className="action-buttons">
                            {getTicketLink(st.booking_link, theater?.id) ? (
                              <a href={getTicketLink(st.booking_link, theater?.id)} target="_blank" rel="noopener noreferrer" className="action-button booking-link">
                                Tickets
                              </a>
                            ) : <em>No booking link</em>}
                            <a href={calLink} target="_blank" rel="noopener noreferrer" className="action-button calendar-link">
                              Add to Calendar
                            </a>
                          </span>
                        </li>
                      );
                    })}
                </ul>
              </div>
            ))
        ) : popupRows.length === 0 ? (
          <p style={{ color: '#999', fontStyle: 'italic' }}>No upcoming screenings scheduled.</p>
        ) : null}

        {/* Pop-Up & Bar Screenings — the Film Clubs portal's rows, deliberately
            SEPARATED from the theater list (Josh 8/3: dividing line + clear
            marking that these are bar/pop-up events, not movie theaters). */}
        {popupRows.length > 0 && (
          <div style={{ marginTop: '28px', borderTop: '2px solid #d4a843', paddingTop: '16px' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '4px', textAlign: 'center' }}>
              Clubs &amp; Community Screenings
              <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', background: '#d4a843', color: '#1a1714', borderRadius: '4px', padding: '3px 8px', verticalAlign: 'middle' }}>POP-UP</span>
            </h3>
            <p style={{ color: '#8a8272', fontSize: '0.85rem', margin: '0 0 12px', textAlign: 'center' }}>
              Screenings from NYC film clubs, libraries, and community venues.
              <Link to="/film-clubs" style={{ color: '#d4a843', marginLeft: '6px' }}>See all clubs &amp; community screenings →</Link>
            </p>
            {Object.entries(popupByDate)
              .sort(([a], [b]) => new Date(a) - new Date(b))
              .map(([date, dateShowtimes]) => (
                <div key={date} className="date-showtimes">
                  <h4>{new Date(date).toLocaleDateString('en-US', { weekday: 'long' })}, {date}</h4>
                  <ul className="showtimes-list">
                    {dateShowtimes
                      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
                      .map(st => {
                        const time = new Date(st.start_at);
                        const timeLabel = time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                        const calLink = createGoogleCalendarLink(movie.title, st.theater, st.start_at);
                        return (
                          <li key={st.id || st.start_at + st.theater}>
                            <strong>{timeLabel}</strong> {st.theater}
                            {st.series && (
                              <span style={{ marginLeft: '6px', fontSize: '0.85rem', color: '#8a8272' }}>
                                Presented by <strong>{st.series}</strong>
                              </span>
                            )}
                            {' '}
                            <span className="action-buttons">
                              {st.booking_link ? (
                                <a href={st.booking_link} target="_blank" rel="noopener noreferrer" className="action-button booking-link">
                                  {st.linkLabel || 'Tickets'}
                                </a>
                              ) : <em>At the door</em>}
                              <a href={calLink} target="_blank" rel="noopener noreferrer" className="action-button calendar-link">
                                Add to Calendar
                              </a>
                            </span>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              ))}
          </div>
        )}

        {upcomingShowtimes.some(st => ['35mm', '70mm', '16mm'].includes(st.film_format)) && (
          <div style={{ marginTop: '20px', padding: '12px 0' }}>
            <Link
              to="/35mm-nyc"
              style={{ color: '#d4a843', textDecoration: 'none', fontSize: '0.95rem', borderBottom: '1px solid #d4a843', paddingBottom: '2px' }}
            >
              See all film print screenings in NYC →
            </Link>
          </div>
        )}

        {/* Past Screening History */}
        {pastShowtimes.length > 0 && (
          <div style={{ marginTop: '32px', borderTop: '1px solid #2a2520', paddingTop: '16px' }}>
            <h3>Past Screenings</h3>
            <p style={{ color: '#8a8272', fontSize: '0.9rem' }}>
              {lastScreening && (
                <>Last screened at {lastScreening.theater} on {new Date(lastScreening.start_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. </>
              )}
              {pastShowtimes.length} past screening{pastShowtimes.length !== 1 ? 's' : ''} recorded
              {pastTheaters.length > 0 && <> at {pastTheaters.slice(0, 5).join(', ')}{pastTheaters.length > 5 ? ` and ${pastTheaters.length - 5} more` : ''}</>}.
            </p>
          </div>
        )}
      </div>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
}

export default FilmPage;
